<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Stack, Select, Text, Alert } from '@Shelly/shelly-design-system';
	import {
		buildRunScriptFlowCardData,
		deviceHasScriptsFromAvailability,
		deviceHasNoScriptsFromAvailability,
		deviceSupportsScripts,
		fetchUnknownRoomScriptsAvailability,
		getCachedDeviceScripts,
		getDeviceRealId,
		loadDeviceScripts,
		mapScriptsToSelectOptions,
		resolveRoomScriptsAvailabilitySync,
		type RunScriptCommand,
		type RunScriptFlowCardData,
		type RoomScriptsAvailability,
		type ScriptSelectOption,
	} from 'src/helpers/device_script_action_helpers';
	import { devices, devices_settings, rooms } from 'src/stores';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { flowEditorState } from '../../../../stores/flow_editor';
	import type { Device } from 'src/types/Device';
	import DelayField from '../Shared/DelayField.svelte';

	export let nodeId: string;

	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: nodeData = (node?.data || {}) as RunScriptFlowCardData & Record<string, unknown>;
	$: delayValue = Number((nodeData.delay as { value?: number } | undefined)?.value ?? 0);
	$: delayUnits = String((nodeData.delay as { units?: string } | undefined)?.units ?? 'sec');

	$: selectedRoom =
		nodeData.roomId != null && nodeData.roomId !== ''
			? String(nodeData.roomId)
			: String($devices[nodeData.deviceId || nodeData.deviceRealId]?.room_id ?? '');
	$: selectedDevice = nodeData.deviceId || nodeData.deviceRealId || '';
	$: selectedScriptId = nodeData.scriptId != null ? String(nodeData.scriptId) : '';
	$: selectedCommand = (nodeData.command === 'stop' ? 'stop' : 'start') as RunScriptCommand;

	let scriptOptions: ScriptSelectOption[] = [];
	let isLoadingScripts = false;
	let loadError = '';
	let scriptsLoadSeq = 0;
	let scriptsLoadedForDeviceKey = '';
	let lastInspectorNodeId = '';
	let isLoadingRoomScripts = false;
	let fetchedScriptsAvailability: RoomScriptsAvailability = {};
	let roomScriptsPrefetchSeq = 0;
	let lastRoomPrefetchKey = '';
	let skipSelectionClearForEdit = false;

	const commandOptions = [
		{ value: 'start', title: 'Start' },
		{ value: 'stop', title: 'Stop' },
		{ value: 'enable', title: 'Enable' },
		{ value: 'disable', title: 'Disable' },
	];

	$: roomOptions = Object.entries($rooms || {}).map(([id, room]) => {
		const roomRecord = room as { name?: string };
		return {
			value: String(parseInt(id, 10)),
			title: roomRecord?.name || `Room ${id}`,
		};
	});

	$: roomDeviceKeys = selectedRoom
		? Object.entries($devices)
				.filter(([, device]) => String((device as Device).room_id) === String(selectedRoom))
				.filter(([, device]) => deviceSupportsScripts(device as Device))
				.map(([deviceKey]) => deviceKey)
		: [];

	$: syncRoomScriptsAvailability =
		selectedRoom && roomDeviceKeys.length > 0
			? resolveRoomScriptsAvailabilitySync(
					roomDeviceKeys,
					$devices as Record<string, Device>,
					$devices_settings,
				).availability
			: {};

	$: deviceScriptsAvailability = {
		...syncRoomScriptsAvailability,
		...fetchedScriptsAvailability,
	};

	$: availableDevices = selectedRoom
		? roomDeviceKeys.map((deviceKey) => {
				const device = $devices[deviceKey] as Device;
				const isSelected = selectedDevice === deviceKey;
				const hasScripts = deviceHasScriptsFromAvailability(deviceScriptsAvailability, device);
				const isDisabled = !hasScripts && !isSelected;

				return {
					value: deviceKey,
					title: device?.name || deviceKey,
					disabled: isDisabled,
				};
			})
		: [];

	$: selectedRoomOption = roomOptions.find((o) => String(o.value) === String(selectedRoom)) ?? null;
	$: selectedDeviceOption = availableDevices.find((o) => o.value === selectedDevice) ?? null;
	$: selectedScriptOption = scriptOptions.find((o) => o.value === selectedScriptId) ?? null;
	$: selectedCommandOption =
		commandOptions.find((o) => o.value === selectedCommand) ?? commandOptions[0];

	const getRoomRecord = (roomId: string) => {
		const numericId = Number(roomId);
		return (
			($rooms as Record<string | number, { name?: string }>)[numericId] ??
			($rooms as Record<string | number, { name?: string }>)[roomId]
		);
	};

	const buildNodeLabel = (data: Partial<RunScriptFlowCardData>): string => {
		const commandLabel =
			data.command === 'stop'
				? 'Stop'
				: data.command === 'enable'
					? 'Enable'
					: data.command === 'disable'
						? 'Disable'
						: 'Run';
		const scriptLabel =
			data.scriptName || (data.scriptId != null ? `script ${data.scriptId}` : 'script');
		const deviceLabel = data.deviceName || data.deviceRealId || 'device';
		return `${commandLabel} ${scriptLabel} on ${deviceLabel}`;
	};

	const selectValue = (detail: unknown): string => {
		if (typeof detail === 'string' || typeof detail === 'number') return String(detail);
		if (detail && typeof detail === 'object' && 'value' in detail) {
			return String((detail as { value: unknown }).value ?? '');
		}
		return '';
	};

	const patchNodeData = (patch: Record<string, unknown>) => {
		if (!node) return;
		flowEditorState.updateNode(nodeId, {
			data: {
				...nodeData,
				label: String(nodeData.label || 'Run Script'),
				sceneData: undefined,
				originalAction: undefined,
				...patch,
			},
		});
	};

	const prefetchScriptsForRoom = async (roomId: string, deviceKeys: string[]) => {
		if (!roomId || deviceKeys.length === 0) {
			fetchedScriptsAvailability = {};
			isLoadingRoomScripts = false;
			return;
		}

		const prefetchSeq = ++roomScriptsPrefetchSeq;
		const devicesMap = $devices as Record<string, Device>;
		const syncResult = resolveRoomScriptsAvailabilitySync(deviceKeys, devicesMap, $devices_settings);

		if (prefetchSeq !== roomScriptsPrefetchSeq) {
			return;
		}

		isLoadingRoomScripts = syncResult.unknownRealIds.length > 0;

		if (syncResult.unknownRealIds.length === 0) {
			isLoadingRoomScripts = false;
			return;
		}

		const fetched = await fetchUnknownRoomScriptsAvailability(
			syncResult.unknownRealIds,
			syncResult.deviceByRealId,
			$devices_settings,
			{
				abortSeq: prefetchSeq,
				getCurrentAbortSeq: () => roomScriptsPrefetchSeq,
			},
		);

		if (prefetchSeq !== roomScriptsPrefetchSeq) {
			return;
		}

		fetchedScriptsAvailability = { ...fetchedScriptsAvailability, ...fetched };
		isLoadingRoomScripts = false;
	};

	$: if (selectedRoom && roomDeviceKeys.length > 0 && selectedRoom !== lastRoomPrefetchKey) {
		lastRoomPrefetchKey = selectedRoom;
		void prefetchScriptsForRoom(selectedRoom, roomDeviceKeys);
	} else if (!selectedRoom) {
		lastRoomPrefetchKey = '';
		roomScriptsPrefetchSeq += 1;
		fetchedScriptsAvailability = {};
		isLoadingRoomScripts = false;
	}

	$: selectedDeviceForClear = $devices[selectedDevice] as Device | undefined;
	$: if (
		!isLoadingRoomScripts &&
		selectedRoom &&
		selectedDeviceForClear &&
		deviceHasNoScriptsFromAvailability(deviceScriptsAvailability, selectedDeviceForClear) &&
		!skipSelectionClearForEdit
	) {
		patchNodeData({
			deviceRealId: '',
			deviceId: '',
			deviceName: undefined,
			scriptId: undefined,
			scriptName: undefined,
			command: selectedCommand,
			label: 'Run Script',
		});
	}

	const handleRoomChange = (detail: unknown) => {
		const roomId = selectValue(detail);
		const room = getRoomRecord(roomId);
		lastRoomPrefetchKey = '';
		fetchedScriptsAvailability = {};
		skipSelectionClearForEdit = false;
		patchNodeData({
			roomId,
			roomName: room?.name,
			deviceRealId: '',
			deviceId: '',
			deviceName: undefined,
			scriptId: undefined,
			scriptName: undefined,
			command: selectedCommand,
			label: 'Run Script',
		});
	};

	const handleDeviceChange = (detail: unknown) => {
		if (isLoadingRoomScripts) {
			return;
		}

		const deviceKey = selectValue(detail);
		const deviceOption = availableDevices.find((option) => option.value === deviceKey);
		const device = $devices[deviceKey] as Device | undefined;

		if (
			!device ||
			deviceOption?.disabled ||
			!deviceHasScriptsFromAvailability(deviceScriptsAvailability, device)
		) {
			return;
		}

		skipSelectionClearForEdit = false;

		const deviceRealId = getDeviceRealId(device);
		patchNodeData({
			deviceRealId,
			deviceId: deviceKey,
			deviceName: device.name,
			roomId: selectedRoom || String(device.room_id ?? ''),
			roomName: getRoomRecord(selectedRoom)?.name,
			channel: 0,
			scriptId: undefined,
			scriptName: undefined,
			command: selectedCommand,
			label: `Run script on ${device.name || deviceRealId}`,
		});
	};

	const handleScriptChange = (detail: unknown) => {
		const scriptId = selectValue(detail);
		const device = $devices[selectedDevice] as Device | undefined;
		if (!device || !scriptId) return;

		const selectedScript = scriptOptions.find((option) => option.value === scriptId);
		const flowData = buildRunScriptFlowCardData(device, Number(scriptId), {
			channel: 0,
			command: selectedCommand,
			scriptName: selectedScript?.title,
			roomId: selectedRoom,
			roomName: getRoomRecord(selectedRoom)?.name,
		});

		patchNodeData({
			...flowData,
			label: buildNodeLabel(flowData),
		});
	};

	const handleCommandChange = (detail: unknown) => {
		const selected = selectValue(detail);
		const command: RunScriptCommand =
			selected === 'stop' || selected === 'enable' || selected === 'disable' ? selected : 'start';
		if (!selectedDevice) {
			patchNodeData({ command, label: 'Run Script' });
			return;
		}

		const device = $devices[selectedDevice] as Device | undefined;
		if (!device) return;

		if (selectedScriptId) {
			const selectedScript = scriptOptions.find((option) => option.value === selectedScriptId);
			const flowData = buildRunScriptFlowCardData(device, Number(selectedScriptId), {
				channel: 0,
				command,
				scriptName: selectedScript?.title,
				roomId: selectedRoom,
				roomName: getRoomRecord(selectedRoom)?.name,
			});
			patchNodeData({ ...flowData, label: buildNodeLabel(flowData) });
			return;
		}

		patchNodeData({
			command,
			label: `Run script on ${device.name || getDeviceRealId(device)}`,
		});
	};

	const applyScriptOptions = (options: ScriptSelectOption[], noScriptsMessage: string) => {
		scriptOptions = options;
		loadError = options.length === 0 ? noScriptsMessage : '';
	};

	const loadScriptsForDevice = async (deviceKey: string) => {
		const noScriptsMessage = $format('no_scripts_on_device', {
			default: 'No scripts on this device',
		});

		if (!deviceKey) {
			scriptsLoadSeq += 1;
			scriptsLoadedForDeviceKey = '';
			scriptOptions = [];
			loadError = '';
			isLoadingScripts = false;
			return;
		}

		if (deviceKey === scriptsLoadedForDeviceKey) {
			return;
		}

		scriptsLoadedForDeviceKey = deviceKey;
		const loadSeq = ++scriptsLoadSeq;

		const device = $devices[deviceKey] as Device | undefined;
		if (!device) {
			applyScriptOptions([], 'Device not found');
			isLoadingScripts = false;
			return;
		}

		const deviceRealId = getDeviceRealId(device);
		const settings = (readDeviceSettings(deviceRealId) || {}) as Record<string, unknown>;
		const sessionScripts = getCachedDeviceScripts(deviceRealId);

		if (sessionScripts) {
			applyScriptOptions(mapScriptsToSelectOptions(sessionScripts, settings), noScriptsMessage);
			isLoadingScripts = false;
			return;
		}

		isLoadingScripts = true;
		const scripts = await loadDeviceScripts(device, $devices_settings);

		if (loadSeq !== scriptsLoadSeq || deviceKey !== scriptsLoadedForDeviceKey) {
			return;
		}

		applyScriptOptions(mapScriptsToSelectOptions(scripts, settings), noScriptsMessage);
		isLoadingScripts = false;
	};

	$: if (nodeId !== lastInspectorNodeId) {
		lastInspectorNodeId = nodeId;
		scriptsLoadedForDeviceKey = '';
		lastRoomPrefetchKey = '';
		roomScriptsPrefetchSeq += 1;
		fetchedScriptsAvailability = {};
		isLoadingRoomScripts = false;
		skipSelectionClearForEdit = nodeData?.scriptId != null;

		if (skipSelectionClearForEdit && nodeData?.deviceId) {
			const editDevice = $devices[nodeData.deviceId] as Device | undefined;
			if (editDevice) {
				fetchedScriptsAvailability = {
					[getDeviceRealId(editDevice)]: true,
				};
			}
		}
	}

	$: if (selectedDevice !== scriptsLoadedForDeviceKey) {
		void loadScriptsForDevice(selectedDevice);
	} else if (!selectedDevice) {
		void loadScriptsForDevice('');
	}
</script>

<Stack gap="m">
	<Text variant="body" size="m">
		{$format('flow_run_script_action', { default: 'Run device script' })}
	</Text>

	<Select
		id="flow-editor-run-script-room"
		icon=""
		aria-label={$format('label_room', { default: 'Room' })}
		label={$format('label_room', { default: 'Room' })}
		options={roomOptions}
		value={selectedRoomOption}
		placeholder={$format('placeholder_select_room', { default: 'Select room' })}
		on:change={(event) => handleRoomChange(event.detail)}
	/>

	<Select
		id="flow-editor-run-script-device"
		icon=""
		aria-label={$format('label_device', { default: 'Device' })}
		label={$format('label_device', { default: 'Device' })}
		options={availableDevices}
		value={selectedDeviceOption}
		disabled={!selectedRoom || isLoadingRoomScripts}
		placeholder={$format('placeholder_select_device', { default: 'Select device' })}
		on:change={(event) => handleDeviceChange(event.detail)}
	/>

	{#if isLoadingRoomScripts}
		<Text size="small" color="secondary">{$format('loading', { default: 'Loading...' })}</Text>
	{/if}

	{#if isLoadingScripts}
		<Text size="small" color="secondary">{$format('loading', { default: 'Loading...' })}</Text>
	{:else if loadError}
		<Alert type="warning" heading={loadError} />
	{:else if selectedDevice}
		<Select
			id="flow-editor-run-script-id"
			icon=""
			aria-label={$format('label_script', { default: 'Script' })}
			label={$format('label_script', { default: 'Script' })}
			options={scriptOptions}
			value={selectedScriptOption}
			placeholder={$format('placeholder_select_script', { default: 'Select script' })}
			on:change={(event) => handleScriptChange(event.detail)}
		/>

		<Select
			id="flow-editor-run-script-command"
			icon=""
			aria-label={$format('label_command', { default: 'Command' })}
			label={$format('label_command', { default: 'Command' })}
			options={commandOptions}
			value={selectedCommandOption}
			on:change={(event) => handleCommandChange(event.detail)}
		/>
	{/if}

	<DelayField
		value={delayValue}
		units={delayUnits}
		on:change={(e) => {
			if (!node) return;
			patchNodeData({ delay: e.detail });
		}}
	/>
</Stack>
